import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, Outlet, useOutletContext } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import { resolveAssetUrl } from "@/lib/asset";
import SEO from "@/components/SEO";
import { 
  Instagram, 
  Facebook, 
  Phone, 
  MapPin, 
  Star, 
  Scissors, 
  Sparkles, 
  Heart,
  Award,
  Users,
  Sparkle,
  Menu,
  X,
  Clock,
  ChevronRight,
  Quote,
  Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AuthProvider } from "@/contexts/AuthContext";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

// Constants
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Brand info
const BRANDS = {
  luxea: {
    name: "Luxéa Esthétique",
    shortName: "Luxéa Esthétique",
    services: ["Cils & Sourcils", "Ongles"],
    email: "luxea.beaute@gmail.com",
    instagram: "https://www.instagram.com/luxea.esthetique.coiffure/",
    facebook: "https://www.facebook.com/share/1BR4hjY3UR/?mibextid=wwXIfr",
    bookingLink: "https://juliette-luxa-esthtique.square.site",
  },
  coiffureyt: {
    name: "Coiffure YT",
    shortName: "Coiffure YT",
    services: ["Coiffure"],
    email: "coiffureyt21c@gmail.com",
    instagram: "https://www.instagram.com/coiffure_yt/",
    facebook: "https://www.facebook.com/share/1D5m15UXw6/?mibextid=wwXIfr",
    bookingLink: "https://coiffureyt.square.site/",
  }
};

// Shared info
const SHARED_INFO = {
  phone: "(514) 383-9853",
  address: "96 Rue Saint-Joseph, Sainte-Thérèse, QC J7E 3L9",
};

// Robust external-link handler.
// In preview iframes some browsers block window.open + target=_blank.
// Strategy: try window.open first; if it's null/blocked, force top-level navigation.
const openExternal = (e, url) => {
  if (!url) return;
  e.preventDefault();
  e.stopPropagation();
  try {
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win || win.closed || typeof win.closed === 'undefined') {
      // popup blocked → break out of iframe
      try {
        if (window.top && window.top !== window.self) {
          window.top.location.href = url;
          return;
        }
      } catch (_) { /* cross-origin: fallthrough */ }
      window.location.href = url;
    }
  } catch (_) {
    window.location.href = url;
  }
};

// Images
const LOGO_URL = "https://customer-assets.emergentagent.com/job_luxea-elegance/artifacts/2gpcfw46_logo_luxea_transparent_large.png";
const HERO_BG = "https://customer-assets.emergentagent.com/job_luxea-elegance/artifacts/52m7myt0_842b3655-b804-4d1c-8b0d-f54996be1066.jpg";
const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_luxea-elegance/artifacts/09ygkkv9_Gemini_Generated_Image_qulnlmqulnlmquln.png";
const ABOUT_IMG_2 = "https://customer-assets.emergentagent.com/job_luxea-elegance/artifacts/5ub6ocbf_e112bc98-aab0-4093-9fbf-9dbf0c91ff46.jpg";
const SERVICE_HAIR = "https://customer-assets.emergentagent.com/job_luxea-elegance/artifacts/orokt8lu_Capture%20d%E2%80%99%C3%A9cran%202026-04-01%20003809.png";
const SERVICE_LASHES = "https://images.unsplash.com/photo-1609978204750-d5018aa878d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxleWVsYXNoJTIwZXh0ZW5zaW9ucyUyMG1ha2V1cHxlbnwwfHx8YmxhY2tfYW5kX3doaXRlfDE3NzUwMTc1MjN8MA&ixlib=rb-4.1.0&q=85";
const SERVICE_NAILS = "https://customer-assets.emergentagent.com/job_luxea-elegance/artifacts/yncmvycu_Capture%20d%E2%80%99%C3%A9cran%202026-04-01%20003521.png";

// Testimonials
const testimonials = [
  { id: 1, name: "Manon Lise", badge: "Local Guide", text: "J'adore ma nouvelle coupe!!! Je reçois beaucoup de beaux commentaires.", rating: 5, brand: "coiffureyt" },
  { id: 2, name: "Camila Riso", text: "J'ai adoré ! La même journée j'ai appelé s'ils y avaient de la place et ils m'ont squeeze in ! Le service était impeccable, l'atmosphère incroyable et le salon très propre !", rating: 5, brand: "coiffureyt" },
  { id: 3, name: "Seak Chea", badge: "Local Guide", text: "Les cils utilisés sont de bonne qualité et durent longtemps. Elle est professionnelle, talentueuse, rapide et expérimentée.", rating: 5, brand: "luxea" },
  { id: 4, name: "Alexandre Marois", badge: "Local Guide", text: "Yrvline. La meilleur pour les coupes de cheveux.", rating: 5, brand: "coiffureyt" },
  { id: 5, name: "Benjamin Parent", text: "Merci Yrvline pour ton service hors pair!!", rating: 5, brand: "coiffureyt" },
  { id: 6, name: "Tommy Lee Charles", text: "Excellent service à chaque fois! Toujours satisfait", rating: 5, brand: "luxea" },
];

const whyUs = [
  { icon: Star, title: "5.0 Étoiles", description: "Note parfaite basée sur 15+ avis clients" },
  { icon: Award, title: "Service Premium", description: "Produits et techniques haut de gamme" },
  { icon: Users, title: "Inclusif", description: "Un espace accueillant pour tous" },
  { icon: Sparkle, title: "Hygiène Impeccable", description: "Normes sanitaires les plus strictes" },
];

const defaultHours = [
  { day: "Lundi", hours: "09h30 - 17h00" },
  { day: "Mardi", hours: "11h00 - 18h00" },
  { day: "Mercredi", hours: "10h00 - 17h00" },
  { day: "Jeudi", hours: "09h00 - 17h00" },
  { day: "Vendredi", hours: "09h00 - 17h00" },
  { day: "Samedi", hours: "Fermé" },
  { day: "Dimanche", hours: "Fermé" },
];

const defaultPricingLuxea = [
  {
    category: "Cils & Sourcils",
    items: [
      { name: "Extension cils classique", price: "90" },
      { name: "Extension cils volume", price: "120" },
      { name: "Remplissage cils", price: "50" },
      { name: "Rehaussement cils", price: "65" },
      { name: "Teinture sourcils", price: "20" },
      { name: "Épilation sourcils", price: "15" },
    ],
  },
  {
    category: "Ongles",
    items: [
      { name: "Manucure classique", price: "30" },
      { name: "Manucure gel", price: "45" },
      { name: "Pédicure classique", price: "40" },
      { name: "Pédicure spa", price: "55" },
      { name: "Pose complète gel", price: "65" },
      { name: "Remplissage gel", price: "45" },
    ],
  },
];

const defaultPricingCoiffure = [
  {
    category: "Coiffure",
    items: [
      { name: "Coupe femme", price: "45" },
      { name: "Coupe homme", price: "25" },
      { name: "Coupe enfant", price: "20" },
      { name: "Coloration", price: "75" },
      { name: "Mèches / Balayage", price: "120" },
      { name: "Brushing", price: "35" },
      { name: "Coiffure événementielle", price: "80" },
    ],
  },
];

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // On non-home pages, the nav is always on a light background → use the scrolled style
  const useLight = !isHome || isScrolled;

  const navLinks = [
    { to: "/", label: "Accueil" },
    { to: "/coiffure-yt", label: "Coiffure YT" },
    { to: "/luxea-esthetique", label: "Luxéa Esthétique" },
    { to: "/galerie", label: "Galerie" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        useLight ? "bg-white/95 backdrop-blur-xl border-b border-black/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_luxea-elegance/artifacts/o2s2v5ov_5fbc0e85-9c80-4e43-9dd4-de76fbca678b.png"
              alt="Luxéa Esthétique & Coiffure YT"
              style={{
                mixBlendMode: useLight ? 'normal' : 'screen',
                filter: useLight ? 'invert(1)' : 'none',
              }}
              className="h-28 md:h-40 w-auto transition-all duration-300 -my-4"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-xs font-medium uppercase tracking-[0.2em] transition-colors ${
                    useLight
                      ? `text-black hover:text-neutral-600 ${isActive ? "border-b border-black" : ""}`
                      : `text-white hover:text-white/70 ${isActive ? "border-b border-white" : ""}`
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/contact">
              <Button
                className={`rounded-none px-6 py-2 text-xs font-medium tracking-wide uppercase transition-all ${
                  useLight ? "bg-black text-white hover:bg-neutral-800" : "bg-white text-black hover:bg-white/90"
                }`}
              >
                Réserver
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden p-2 ${useLight ? "text-black" : "text-white"}`}
            aria-label="Menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 w-full bg-white border-b border-black/10 py-6 px-6">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMenuOpen(false)}
                  className="text-sm font-medium uppercase tracking-[0.15em] text-black hover:text-neutral-600 py-2"
                >
                  {link.label}
                </Link>
              ))}
              <Separator className="my-2" />
              <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="w-full">
                <Button className="w-full rounded-none bg-black text-white hover:bg-neutral-800 py-3 text-xs font-medium tracking-wide uppercase">
                  Réserver maintenant
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname]);
  return null;
};

// Hero Section
const Hero = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="Intérieur du salon Luxéa Esthétique & Coiffure YT à Sainte-Thérèse" className="w-full h-full object-cover grayscale" loading="eager" />
        <div className="absolute inset-0 bg-black/60" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center text-white mt-14">
        <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-none mb-4 animate-fadeInUp">
          Luxéa Esthétique
        </h1>
        <div className="flex items-center justify-center gap-4 mb-4">
          <div className="h-px w-12 bg-white/50" />
          <span className="text-white/70 text-sm uppercase tracking-[0.3em]">&</span>
          <div className="h-px w-12 bg-white/50" />
        </div>
        <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl md:text-4xl font-light tracking-tight leading-none mb-8 animate-fadeInUp delay-100">
          Coiffure YT
        </h2>
        <p className="font-['Manrope'] text-base md:text-lg font-light text-white/80 max-w-xl mx-auto mb-10 animate-fadeInUp delay-200">
          Salon de coiffure et esthétique à Sainte-Thérèse — coiffure, coloration, extensions capillaires, extensions de cils, ongles et soins esthétiques sous un même toit.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fadeInUp delay-300">
          <Link to="/coiffure-yt">
            <Button className="rounded-none bg-white text-black hover:bg-white/90 px-8 py-4 text-sm font-medium tracking-wide uppercase">
              Coiffure YT
            </Button>
          </Link>
          <Link to="/luxea-esthetique">
            <Button variant="outline" className="rounded-none border-white text-white hover:bg-white hover:text-black px-8 py-4 text-sm font-medium tracking-wide uppercase">
              Luxéa Esthétique
            </Button>
          </Link>
        </div>

        <div className="mt-16 inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 animate-fadeInUp delay-400">
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-white text-white" />
            ))}
          </div>
          <span className="text-sm font-medium">5.0 · 15+ avis</span>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-px h-16 bg-gradient-to-b from-white/0 via-white/50 to-white" />
      </div>
    </section>
  );
};

// About Section
const About = () => {
  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <div className="order-2 md:order-1">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500 mb-4">Notre Histoire</p>
            <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light tracking-tight leading-tight mb-6">
              Deux passions,<br />Un salon
            </h1>
            <div className="space-y-4 text-base leading-relaxed font-light text-neutral-600">
              <p>
                Depuis <strong className="text-black font-medium">2016</strong>, <strong className="text-black">Luxéa Esthétique</strong> et <strong className="text-black">Coiffure YT</strong> unissent leur
                expertise et leur passion pour offrir une expérience beauté complète sous un même toit.
              </p>
              <p>
                Fortes de plus de <strong className="text-black font-medium">10 ans d'expérience</strong> dans l'industrie,
                nos professionnelles proposent une approche personnalisée adaptée aux besoins de chaque cliente.
              </p>
              <p>
                Situé au cœur de Sainte-Thérèse, notre salon vous accueille dans une ambiance chaleureuse et professionnelle
                où expertise, créativité et attention aux détails se rencontrent pour vous offrir une expérience unique.
              </p>
            </div>
            <div className="mt-8 flex items-center gap-4">
              <a href={`tel:${SHARED_INFO.phone.replace(/[^0-9]/g, "")}`}>
                <Button className="rounded-none bg-black text-white hover:bg-neutral-800 px-6 py-3 text-sm font-medium tracking-wide uppercase">
                  <Phone className="w-4 h-4 mr-2" />
                  Appeler
                </Button>
              </a>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <div className="relative">
              <div className="absolute -inset-4 border border-black/10" />
              <img src={ABOUT_IMG_2} alt="Salon de coiffure et esthétique Luxéa & Coiffure YT à Sainte-Thérèse — intérieur" className="w-full aspect-[4/5] object-cover grayscale relative" loading="lazy" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Brand Section Component (reusable for both brands)
// === Rich-text helper for policy content ===
// Supports **bold** segments and "\n\n" paragraph breaks.
const renderRichText = (text) => {
  if (!text) return null;
  const paragraphs = String(text).split(/\n\n+/);
  return paragraphs.map((p, pi) => {
    const parts = p.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    return (
      <p key={pi} className={pi > 0 ? "mt-3" : ""}>
        {parts.map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i} className="font-medium text-black">{part.slice(2, -2)}</strong>;
          }
          return <React.Fragment key={i}>{part}</React.Fragment>;
        })}
      </p>
    );
  });
};

// Same as above but for dark backgrounds (highlight box) where strong should be white
const renderRichTextOnDark = (text) => {
  if (!text) return null;
  const paragraphs = String(text).split(/\n\n+/);
  return paragraphs.map((p, pi) => {
    const parts = p.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    return (
      <p key={pi} className={pi > 0 ? "mt-3" : ""}>
        {parts.map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i} className="font-medium text-white">{part.slice(2, -2)}</strong>;
          }
          return <React.Fragment key={i}>{part}</React.Fragment>;
        })}
      </p>
    );
  });
};

// Brand-specific Policy (inline, shown between pricing and Réserver button)
// Fully dynamic: rendered from `policy` doc fetched from /api/public/policies.
const BrandPolicy = ({ policy }) => {
  if (!policy || !policy.title) return null;
  const sections = Array.isArray(policy.sections) ? policy.sections : [];
  const hasHighlight = policy.highlight_title || policy.highlight_content || policy.highlight_footer;
  return (
    <div className="max-w-4xl mx-auto mt-16 pt-12 border-t border-neutral-200" data-testid="brand-policy">
      <div className="text-center mb-10">
        {policy.label && (
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500 mb-3">{policy.label}</p>
        )}
        <h3 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-light tracking-tight">
          {policy.title}
        </h3>
        {policy.intro && (
          <p className="mt-4 text-sm font-light text-neutral-600 max-w-2xl mx-auto">
            {policy.intro}
          </p>
        )}
      </div>

      {sections.length > 0 && (
        <div className="grid md:grid-cols-2 gap-6">
          {sections.map((s, idx) => (
            <div key={s.id || idx} className="bg-white border border-neutral-200 p-6">
              <h4 className="font-['Cormorant_Garamond'] text-xl font-light mb-4 pb-2 border-b border-neutral-200">{s.title}</h4>
              <div className="text-sm font-light text-neutral-700 leading-relaxed">
                {renderRichText(s.content)}
              </div>
            </div>
          ))}
        </div>
      )}

      {hasHighlight && (
        <div className="mt-6 bg-black text-white p-6 md:p-8">
          {policy.highlight_title && (
            <h4 className="font-['Cormorant_Garamond'] text-xl md:text-2xl font-light mb-5 pb-2 border-b border-white/20">{policy.highlight_title}</h4>
          )}
          {policy.highlight_content && (
            <div className="text-sm font-light text-white/85 leading-relaxed">
              {renderRichTextOnDark(policy.highlight_content)}
            </div>
          )}
          {policy.highlight_footer && (
            <p className="mt-4 pt-4 border-t border-white/15 text-sm italic text-white/85">
              {policy.highlight_footer}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const BrandSection = ({ brand, brandKey, pricing, dynamicServices, policy }) => {
  const brandInfo = BRANDS[brandKey];
  const isCoiffure = brandKey === "coiffureyt";

  // Default icons used when admin hasn't customised
  const defaultIcons = { coiffureyt: Scissors, luxea: Sparkles };

  // Fallback static services when API hasn't returned anything yet
  const fallbackServices = isCoiffure ? [
    { id: "fb-coiffure", title: "Coiffure", description: "Coupes, colorations, mèches et coiffures événementielles par nos experts.", image_url: SERVICE_HAIR }
  ] : [
    { id: "fb-cils", title: "Cils & Sourcils", description: "Extensions de cils, rehaussement, teinture et mise en forme des sourcils.", image_url: SERVICE_LASHES },
    { id: "fb-ongles", title: "Ongles", description: "Manucure, pédicure, pose de gel et nail art par nos techniciennes certifiées.", image_url: SERVICE_NAILS }
  ];

  const servicesData = (dynamicServices && dynamicServices.length > 0) ? dynamicServices : fallbackServices;
  const ServiceIcon = defaultIcons[brandKey] || Sparkles;

  return (
    <section id={brandKey} className={`py-24 md:py-32 ${isCoiffure ? 'bg-neutral-50' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500 mb-4">
            {isCoiffure ? "Coiffure & Style" : "Beauté & Esthétique"}
          </p>
          <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light tracking-tight mb-4">
            {brandInfo.name}
          </h1>
          <div className="flex items-center justify-center gap-4 mt-6">
            <a href={brandInfo.instagram} target="_blank" rel="noopener noreferrer" aria-label={`Instagram ${brandInfo.name}`} className="text-black hover:text-neutral-600 transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <a href={brandInfo.facebook} target="_blank" rel="noopener noreferrer" aria-label={`Facebook ${brandInfo.name}`} className="text-black hover:text-neutral-600 transition-colors">
              <Facebook className="w-5 h-5" />
            </a>
            <a
              href={`https://mail.google.com/mail/?view=cm&fs=1&to=${brandInfo.email}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Envoyer un courriel à ${brandInfo.name}`}
              className="text-black hover:text-neutral-600 transition-colors"
            >
              <Mail className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Services */}
        <div className={`grid ${servicesData.length === 1 ? 'md:grid-cols-1 max-w-md mx-auto' : servicesData.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'} gap-8 mb-16`}>
          {servicesData.map((service) => (
            <div key={service.id} className="group relative overflow-hidden bg-white border border-neutral-200 hover:shadow-xl transition-all duration-500">
              <div className="relative aspect-[3/4] overflow-hidden">
                <img
                  src={resolveAssetUrl(service.image_url) || ''}
                  alt={`${service.title} — ${brandInfo.name} à Sainte-Thérèse`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-500" />
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <ServiceIcon className="w-5 h-5 text-black" />
                  <h3 className="font-['Cormorant_Garamond'] text-2xl font-light tracking-tight">{service.title}</h3>
                </div>
                <p className="text-sm font-light text-neutral-600 leading-relaxed">{service.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Pricing */}
        <div className="bg-white border border-neutral-200 p-8 md:p-12">
          <h3 className="font-['Cormorant_Garamond'] text-2xl font-light tracking-tight mb-8 text-center">
            Tarifs {brandInfo.shortName}
          </h3>
          <div className={`grid ${pricing.length > 1 ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-md mx-auto'} gap-12`}>
            {pricing.map((category, index) => (
              <div key={index}>
                <h4 className="font-medium mb-4 pb-3 border-b-2 border-black">{category.category}</h4>
                <div className="space-y-0">
                  {category.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-center justify-between py-3 border-b border-neutral-200 last:border-0">
                      <span className="text-sm font-light text-neutral-700">{item.name}</span>
                      <span className="text-sm font-medium text-black">{/^[+\-—]?\d+(\.\d+)?(-\d+)?\+?$/.test(item.price) ? `${item.price}$` : item.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <BrandPolicy policy={policy} />
          <div className="text-center mt-8">
            <a href={brandInfo.bookingLink} target="_blank" rel="noopener noreferrer">
              <Button className="rounded-none bg-black text-white hover:bg-neutral-800 px-10 py-3 text-sm font-medium tracking-wide uppercase">
                Réserver {brandInfo.shortName}
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyUs = () => {
  return (
    <section className="py-24 md:py-32 bg-black text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60 mb-4">Notre Engagement</p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light tracking-tight">Pourquoi Nous Choisir</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {whyUs.map((feature, index) => (
            <div key={index} className="text-center p-8 border border-white/10 hover:border-white/30 transition-colors">
              <feature.icon className="w-10 h-10 mx-auto mb-6 text-white" />
              <h3 className="font-['Cormorant_Garamond'] text-xl font-light tracking-tight mb-3">{feature.title}</h3>
              <p className="text-sm font-light text-white/60">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const Testimonials = ({ brand }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const filtered = brand ? testimonials.filter(t => t.brand === brand) : testimonials;
  const items = filtered.length > 0 ? filtered : testimonials;
  const safeIndex = Math.min(currentIndex, items.length - 1);

  return (
    <section className="py-24 md:py-32 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500 mb-4">Ce Que Disent Nos Clients</p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light tracking-tight">Témoignages</h2>
        </div>

        <div className="hidden lg:grid lg:grid-cols-3 gap-8">
          {items.slice(0, 3).map((testimonial) => (
            <div key={testimonial.id} className="p-8 md:p-10 bg-white border border-neutral-200 flex flex-col justify-between">
              <div>
                <Quote className="w-8 h-8 text-neutral-200 mb-6" />
                <p className="text-base font-light text-neutral-700 leading-relaxed mb-6">"{testimonial.text}"</p>
              </div>
              <div>
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-black text-black" />
                  ))}
                </div>
                <p className="text-sm font-medium text-black">{testimonial.name}</p>
                {testimonial.badge && <p className="text-xs text-neutral-500">{testimonial.badge}</p>}
                <p className="text-xs text-neutral-400 mt-1">{testimonial.brand === 'coiffureyt' ? 'Coiffure YT' : 'Luxéa Esthétique'}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:hidden">
          <div className="p-8 bg-white border border-neutral-200">
            <Quote className="w-8 h-8 text-neutral-200 mb-6" />
            <p className="text-base font-light text-neutral-700 leading-relaxed mb-6">"{items[safeIndex].text}"</p>
            <div className="flex items-center gap-1 mb-3">
              {[...Array(items[safeIndex].rating)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-black text-black" />
              ))}
            </div>
            <p className="text-sm font-medium text-black">{items[safeIndex].name}</p>
            {items[safeIndex].badge && <p className="text-xs text-neutral-500">{items[safeIndex].badge}</p>}
          </div>
          <div className="flex justify-center gap-2 mt-6">
            {items.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${index === safeIndex ? "bg-black" : "bg-neutral-300"}`}
                aria-label={`Avis ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const Gallery = ({ galleryImages }) => {
  const defaultGalleryImages = [
    { id: "1", url: "https://customer-assets.emergentagent.com/job_luxea-elegance/artifacts/8nksficv_Capture%20d%E2%80%99%C3%A9cran%202026-04-01%20003447.png" },
    { id: "2", url: "https://customer-assets.emergentagent.com/job_luxea-elegance/artifacts/kdch0nnz_Capture%20d%E2%80%99%C3%A9cran%202026-04-01%20003521.png" },
    { id: "3", url: "https://customer-assets.emergentagent.com/job_luxea-elegance/artifacts/tqzrvfzv_Capture%20d%E2%80%99%C3%A9cran%202026-04-01%20003653.png" },
    { id: "4", url: "https://customer-assets.emergentagent.com/job_luxea-elegance/artifacts/llxurdiy_Capture%20d%E2%80%99%C3%A9cran%202026-04-01%20003809.png" },
    { id: "5", url: "https://customer-assets.emergentagent.com/job_luxea-elegance/artifacts/by0djsdt_Capture%20d%E2%80%99%C3%A9cran%202026-04-01%20004008.png" },
    { id: "6", url: ABOUT_IMG },
  ];

  const images = galleryImages.length > 0 ? galleryImages : defaultGalleryImages;

  return (
    <section id="gallery" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500 mb-4">Notre Univers</p>
          <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light tracking-tight">Galerie</h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((image, index) => (
            <div key={image.id || index} className={`relative overflow-hidden group ${index === 0 ? "col-span-2 row-span-2" : ""}`}>
              <img
                src={resolveAssetUrl(image.url)}
                alt={image.alt || `Réalisation salon Luxéa Esthétique & Coiffure YT à Sainte-Thérèse #${index + 1}`}
                className="w-full aspect-square object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors duration-500" />
            </div>
          ))}
        </div>

        <div className="text-center mt-12 flex flex-col sm:flex-row items-center justify-center gap-6">
          <a href={BRANDS.luxea.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 text-sm font-medium uppercase tracking-[0.15em] text-black hover:text-neutral-600 transition-colors">
            <Instagram className="w-5 h-5" />
            @luxea.co
          </a>
          <a href={BRANDS.coiffureyt.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 text-sm font-medium uppercase tracking-[0.15em] text-black hover:text-neutral-600 transition-colors">
            <Instagram className="w-5 h-5" />
            @coiffure_yt
          </a>
        </div>
      </div>
    </section>
  );
};

// Policy Section — generic /conditions page showing both brands' policies dynamically
const Policy = ({ policies }) => {
  const luxea = policies?.luxea;
  const coiffure = policies?.coiffure;
  return (
    <section id="politique" className="py-24 md:py-32 bg-white">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500 mb-4">Conditions</p>
          <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light tracking-tight">
            Politiques de rendez-vous
          </h1>
          <div className="mx-auto mt-6 h-px w-16 bg-black/30" />
          <p className="mt-6 max-w-2xl mx-auto text-sm font-light text-neutral-600 leading-relaxed">
            Conditions de réservation, d'annulation et de retard pour chacune de nos marques.
          </p>
        </div>

        <div className="space-y-20">
          {luxea && (
            <div>
              <div className="flex items-center gap-4 mb-2">
                <div className="h-px flex-1 bg-neutral-200" />
                <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-light">Luxéa Esthétique</h3>
                <div className="h-px flex-1 bg-neutral-200" />
              </div>
              <BrandPolicy policy={luxea} />
            </div>
          )}
          {coiffure && (
            <div>
              <div className="flex items-center gap-4 mb-2">
                <div className="h-px flex-1 bg-neutral-200" />
                <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-light">Coiffure YT</h3>
                <div className="h-px flex-1 bg-neutral-200" />
              </div>
              <BrandPolicy policy={coiffure} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const Contact = ({ hours }) => {
  return (
    <section id="contact" className="py-24 md:py-32 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500 mb-4">Contactez-Nous</p>
          <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light tracking-tight">Réserver</h1>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Coiffure YT */}
          <div className="bg-white border border-neutral-200 p-8">
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-light mb-6">Coiffure YT</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-black" />
                <a
                  href={`https://mail.google.com/mail/?view=cm&fs=1&to=${BRANDS.coiffureyt.email}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm hover:text-neutral-600"
                >{BRANDS.coiffureyt.email}</a>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-black" />
                <a href={`tel:${SHARED_INFO.phone.replace(/[^0-9]/g, "")}`} className="text-sm hover:text-neutral-600">{SHARED_INFO.phone}</a>
              </div>
              <div className="flex items-center gap-4 pt-4">
                <a href={BRANDS.coiffureyt.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram Coiffure YT" className="w-10 h-10 border border-black flex items-center justify-center text-black hover:bg-black hover:text-white transition-all">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href={BRANDS.coiffureyt.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook Coiffure YT" className="w-10 h-10 border border-black flex items-center justify-center text-black hover:bg-black hover:text-white transition-all">
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </div>
            <a href={BRANDS.coiffureyt.bookingLink} target="_blank" rel="noopener noreferrer" className="block mt-6">
              <Button className="w-full rounded-none bg-black text-white hover:bg-neutral-800 py-3 text-sm font-medium tracking-wide uppercase">
                Réserver Coiffure YT
              </Button>
            </a>
          </div>

          {/* Luxea */}
          <div className="bg-white border border-neutral-200 p-8">
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-light mb-6">Luxéa Esthétique</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-black" />
                <a
                  href={`https://mail.google.com/mail/?view=cm&fs=1&to=${BRANDS.luxea.email}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm hover:text-neutral-600"
                >{BRANDS.luxea.email}</a>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-black" />
                <a href={`tel:${SHARED_INFO.phone.replace(/[^0-9]/g, "")}`} className="text-sm hover:text-neutral-600">{SHARED_INFO.phone}</a>
              </div>
              <div className="flex items-center gap-4 pt-4">
                <a href={BRANDS.luxea.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram Luxéa Esthétique" className="w-10 h-10 border border-black flex items-center justify-center text-black hover:bg-black hover:text-white transition-all">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href={BRANDS.luxea.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook Luxéa Esthétique" className="w-10 h-10 border border-black flex items-center justify-center text-black hover:bg-black hover:text-white transition-all">
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </div>
            <a href={BRANDS.luxea.bookingLink} target="_blank" rel="noopener noreferrer" className="block mt-6">
              <Button className="w-full rounded-none bg-black text-white hover:bg-neutral-800 py-3 text-sm font-medium tracking-wide uppercase">
                Réserver Luxéa Esthétique
              </Button>
            </a>
          </div>
        </div>

        {/* Shared Info */}
        <div className="mt-12 grid md:grid-cols-2 gap-8">
          <div className="bg-white border border-neutral-200 p-8">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Adresse
            </h3>
            <p className="text-sm text-neutral-600 mb-4">{SHARED_INFO.address}</p>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2791.8431774747187!2d-73.84271892345447!3d45.63809067107872!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc921d3d1c5f0a7%3A0x8e5b9c8e8e8e8e8e!2s96%20Rue%20Saint-Joseph%2C%20Sainte-Th%C3%A9r%C3%A8se%2C%20QC%20J7E%203L9!5e0!3m2!1sen!2sca!4v1234567890123"
              width="100%"
              height="200"
              style={{ border: 0, filter: "grayscale(100%)" }}
              allowFullScreen=""
              loading="lazy"
              title="Carte — Luxéa Esthétique & Coiffure YT à Sainte-Thérèse"
            />
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=96+Rue+Saint-Joseph+Sainte-Th%C3%A9r%C3%A8se+QC+J7E+3L9"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium uppercase tracking-wider text-black hover:underline"
                data-testid="get-directions-link"
              >
                Itinéraire Google Maps →
              </a>
              <a
                href="https://www.google.com/search?q=Lux%C3%A9a+Esth%C3%A9tique+Coiffure+YT+Sainte-Th%C3%A9r%C3%A8se+avis"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium uppercase tracking-wider text-black hover:underline"
                data-testid="google-reviews-link"
              >
                Voir les avis Google →
              </a>
            </div>
          </div>
          <div className="bg-white border border-neutral-200 p-8">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Heures d'ouverture
            </h3>
            <div className="space-y-2">
              {hours.map((item, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-neutral-600">{item.day}</span>
                  <span className={item.hours === "Fermé" ? "text-neutral-400" : "text-black"}>{item.hours}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="py-16 md:py-24 bg-black text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <h3 className="font-['Cormorant_Garamond'] text-2xl font-light mb-2">Luxéa Esthétique</h3>
          <span className="text-white/50">&</span>
          <h3 className="font-['Cormorant_Garamond'] text-2xl font-light mt-2">Coiffure YT</h3>
          <p className="text-sm font-light text-white/60 max-w-md mx-auto mt-4">
            Des soins professionnels, une attention personnalisée, une beauté qui vous ressemble.
          </p>
        </div>

        <div className="flex justify-center gap-4 mb-12">
          <a href={BRANDS.luxea.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram Luxéa Esthétique" className="w-10 h-10 border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all">
            <Instagram className="w-4 h-4" />
          </a>
          <a href={BRANDS.luxea.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook Luxéa Esthétique" className="w-10 h-10 border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all">
            <Facebook className="w-4 h-4" />
          </a>
          <a href={BRANDS.coiffureyt.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram Coiffure YT" className="w-10 h-10 border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all">
            <Instagram className="w-4 h-4" />
          </a>
          <a href={BRANDS.coiffureyt.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook Coiffure YT" className="w-10 h-10 border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all">
            <Facebook className="w-4 h-4" />
          </a>
          <a href={`tel:${SHARED_INFO.phone.replace(/[^0-9]/g, "")}`} aria-label="Appeler le salon" className="w-10 h-10 border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all">
            <Phone className="w-4 h-4" />
          </a>
        </div>

        <Separator className="bg-white/10 mb-8" />

        <div className="text-center">
          <p className="text-xs text-white/40">© {currentYear} Luxéa Esthétique / Coiffure YT. Tous droits réservés.</p>
          <p className="text-xs text-white/40 mt-2">{SHARED_INFO.address} · {SHARED_INFO.phone}</p>
        </div>
      </div>
    </footer>
  );
};

// === Promotions Section (public site) ===
const PromotionsSection = ({ promotions }) => {
  if (!promotions || promotions.length === 0) return null;

  const luxeaPromos = promotions.filter(p => p.brand === 'luxea');
  const coiffurePromos = promotions.filter(p => p.brand === 'coiffure');

  const PromoCard = ({ promo, brandLabel }) => (
    <div className="group bg-white border border-neutral-200 hover:border-black transition-colors flex flex-col">
      <div className="relative overflow-hidden">
        <img
          src={resolveAssetUrl(promo.image_url)}
          alt={promo.description ? `${promo.description} — ${brandLabel} à Sainte-Thérèse` : `Promotion ${brandLabel} à Sainte-Thérèse`}
          loading="lazy"
          className="w-full aspect-[3/4] object-cover group-hover:scale-[1.03] transition-transform duration-700"
        />
        <span className="absolute top-3 left-3 bg-black/90 text-white text-[10px] font-semibold uppercase tracking-[0.15em] px-3 py-1">
          {brandLabel}
        </span>
      </div>
      <div className="p-5 flex-1 flex flex-col gap-4">
        {promo.description && (
          <p className="text-sm font-light text-neutral-700 leading-relaxed line-clamp-4">
            {promo.description}
          </p>
        )}
        {promo.booking_link && (
          <a
            href={promo.booking_link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-auto"
          >
            <Button className="w-full rounded-none bg-black text-white hover:bg-neutral-800 text-xs font-medium tracking-wide uppercase py-3">
              Réserver
            </Button>
          </a>
        )}
      </div>
    </div>
  );

  const BrandGroup = ({ items, label }) => {
    if (items.length === 0) return null;
    return (
      <div>
        <div className="flex items-center gap-4 mb-6">
          <div className="h-px flex-1 bg-neutral-200" />
          <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-light">{label}</h3>
          <div className="h-px flex-1 bg-neutral-200" />
        </div>
        {/* Mobile: horizontal scroll, Desktop: grid */}
        <div className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-5 overflow-x-auto md:overflow-visible snap-x snap-mandatory pb-4 md:pb-0 -mx-6 px-6 md:mx-0 md:px-0">
          {items.map(p => (
            <div key={p.id} className="min-w-[75%] sm:min-w-[55%] md:min-w-0 snap-start">
              <PromoCard promo={p} brandLabel={label} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <section id="promotions" className="py-24 md:py-32 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500 mb-4">Offres limitées</p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light tracking-tight">
            Promotions en cours
          </h2>
          <div className="mx-auto mt-6 h-px w-16 bg-black/30" />
        </div>

        <div className="space-y-16">
          <BrandGroup items={luxeaPromos} label="Luxéa Esthétique" />
          <BrandGroup items={coiffurePromos} label="Coiffure YT" />
        </div>
      </div>
    </section>
  );
};

// === Layout wrapper: Nav + shared data + Outlet + Footer ===
const Layout = () => {
  const [pricingLuxea, setPricingLuxea] = useState(defaultPricingLuxea);
  const [pricingCoiffure, setPricingCoiffure] = useState(defaultPricingCoiffure);
  const [hours, setHours] = useState(defaultHours);
  const [galleryImages, setGalleryImages] = useState([]);
  const [serviceImages, setServiceImages] = useState({});
  const [promotions, setPromotions] = useState([]);
  const [dynamicServices, setDynamicServices] = useState({ luxea: [], coiffure: [] });
  const [policies, setPolicies] = useState({ luxea: null, coiffure: null });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pricingRes, hoursRes, galleryRes, servicesRes, promosRes, dynSvcRes, policiesRes] = await Promise.all([
          axios.get(`${API_URL}/api/public/pricing`).catch(() => ({ data: {} })),
          axios.get(`${API_URL}/api/public/hours`).catch(() => ({ data: { hours: defaultHours } })),
          axios.get(`${API_URL}/api/public/gallery`).catch(() => ({ data: { images: [] } })),
          axios.get(`${API_URL}/api/public/services/images`).catch(() => ({ data: { images: {} } })),
          axios.get(`${API_URL}/api/public/promotions`).catch(() => ({ data: { promotions: [] } })),
          axios.get(`${API_URL}/api/public/services`).catch(() => ({ data: { services: { luxea: [], coiffure: [] } } })),
          axios.get(`${API_URL}/api/public/policies`).catch(() => ({ data: { policies: { luxea: null, coiffure: null } } }))
        ]);
        if (pricingRes.data.luxea) setPricingLuxea(pricingRes.data.luxea);
        if (pricingRes.data.coiffure) setPricingCoiffure(pricingRes.data.coiffure);
        if (hoursRes.data.hours) setHours(hoursRes.data.hours);
        if (galleryRes.data.images) setGalleryImages(galleryRes.data.images);
        if (servicesRes.data.images) setServiceImages(servicesRes.data.images);
        if (promosRes.data.promotions) setPromotions(promosRes.data.promotions);
        if (dynSvcRes.data.services) setDynamicServices(dynSvcRes.data.services);
        if (policiesRes.data.policies) setPolicies(policiesRes.data.policies);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <>
      <ScrollToTop />
      <Navigation />
      <main>
        <Outlet context={{ pricingLuxea, pricingCoiffure, hours, galleryImages, serviceImages, promotions, dynamicServices, policies }} />
      </main>
      <Footer />
    </>
  );
};

// Small helper: adds top padding so content isn't hidden under the fixed nav
const PageWrap = ({ children, className = "" }) => (
  <div className={`pt-24 md:pt-28 ${className}`}>{children}</div>
);

// === Pages ===
const HomePage = () => {
  const { promotions } = useOutletContext();
  return (
    <>
      <SEO
        title="Salon de coiffure et esthétique à Sainte-Thérèse"
        description="Luxéa Esthétique & Coiffure à Sainte-Thérèse vous offre des services de coiffure, coloration, extensions capillaires, extensions de cils, soins esthétiques, manucure et pédicure. Prenez rendez-vous dès aujourd'hui."
        keywords="salon de coiffure Sainte-Thérèse, coiffure Sainte-Thérèse, esthéticienne Sainte-Thérèse, extensions capillaires Sainte-Thérèse, extensions de cils Sainte-Thérèse, salon de beauté Sainte-Thérèse"
        path="/"
      />
      <Hero />
      <About />
      <PromotionsSection promotions={promotions} />
    </>
  );
};

const AboutPage = () => (
  <PageWrap>
    <SEO
      title="À propos — Notre histoire"
      description="Depuis 2016, Luxéa Esthétique et Coiffure YT réunissent leurs expertises à Sainte-Thérèse pour offrir coiffure, esthétique, extensions capillaires, cils et ongles dans un salon haut de gamme."
      keywords="salon de beauté Sainte-Thérèse, à propos Luxéa, Coiffure YT Sainte-Thérèse, salon coiffure esthétique"
      path="/a-propos"
      breadcrumbs={[
        { name: "Accueil", url: "/" },
        { name: "À propos", url: "/a-propos" },
      ]}
    />
    <About />
  </PageWrap>
);

const CoiffureYtPage = () => {
  const { pricingCoiffure, dynamicServices, policies } = useOutletContext();
  return (
    <PageWrap>
      <SEO
        title="Coiffure YT — Salon de coiffure à Sainte-Thérèse"
        description="Coiffure YT à Sainte-Thérèse : coupe de cheveux, coloration, mèches, balayage, extensions capillaires et coiffure événementielle. Réservez en ligne."
        keywords="coiffure Sainte-Thérèse, salon coiffure Sainte-Thérèse, coloration Sainte-Thérèse, mèches Sainte-Thérèse, balayage Sainte-Thérèse, extensions capillaires Sainte-Thérèse, Coiffure YT"
        path="/coiffure-yt"
        breadcrumbs={[
          { name: "Accueil", url: "/" },
          { name: "Coiffure YT", url: "/coiffure-yt" },
        ]}
      />
      <BrandSection brandKey="coiffureyt" pricing={pricingCoiffure} dynamicServices={dynamicServices?.coiffure || []} policy={policies?.coiffure} />
      <Testimonials brand="coiffureyt" />
    </PageWrap>
  );
};

const LuxeaPage = () => {
  const { pricingLuxea, dynamicServices, policies } = useOutletContext();
  return (
    <PageWrap>
      <SEO
        title="Luxéa Esthétique — Cils, sourcils et ongles à Sainte-Thérèse"
        description="Luxéa Esthétique à Sainte-Thérèse : extensions de cils, rehaussement, teinture et épilation de sourcils, microblading, manucure, pédicure et pose de gel."
        keywords="extensions de cils Sainte-Thérèse, esthéticienne Sainte-Thérèse, sourcils Sainte-Thérèse, manucure Sainte-Thérèse, pédicure Sainte-Thérèse, microblading Sainte-Thérèse, Luxéa Esthétique"
        path="/luxea-esthetique"
        breadcrumbs={[
          { name: "Accueil", url: "/" },
          { name: "Luxéa Esthétique", url: "/luxea-esthetique" },
        ]}
      />
      <BrandSection brandKey="luxea" pricing={pricingLuxea} dynamicServices={dynamicServices?.luxea || []} policy={policies?.luxea} />
      <Testimonials brand="luxea" />
    </PageWrap>
  );
};

const GalleryPage = () => {
  const { galleryImages } = useOutletContext();
  return (
    <PageWrap>
      <SEO
        title="Galerie photos — Nos réalisations"
        description="Découvrez la galerie de Luxéa Esthétique & Coiffure YT : réalisations de coiffure, coloration, extensions de cils, ongles et soins esthétiques à Sainte-Thérèse."
        keywords="galerie salon Sainte-Thérèse, réalisations coiffure, photos extensions cils, portfolio esthétique Sainte-Thérèse"
        path="/galerie"
        breadcrumbs={[
          { name: "Accueil", url: "/" },
          { name: "Galerie", url: "/galerie" },
        ]}
      />
      <Gallery galleryImages={galleryImages} />
    </PageWrap>
  );
};

const PolicyPage = () => {
  const { policies } = useOutletContext();
  return (
    <PageWrap>
      <SEO
        title="Politiques de rendez-vous et d'annulation"
        description="Politiques de réservation, d'annulation et de retard pour Luxéa Esthétique et Coiffure YT à Sainte-Thérèse. Dépôts, cartes-cadeaux et conditions générales."
        path="/conditions"
        breadcrumbs={[
          { name: "Accueil", url: "/" },
          { name: "Conditions", url: "/conditions" },
        ]}
      />
      <Policy policies={policies} />
    </PageWrap>
  );
};

const ContactPage = () => {
  const { hours } = useOutletContext();
  return (
    <PageWrap>
      <SEO
        title="Contact et réservation à Sainte-Thérèse"
        description="Prenez rendez-vous chez Luxéa Esthétique & Coiffure YT au 96 Rue Saint-Joseph, Sainte-Thérèse. Téléphone : (514) 383-9853. Réservation en ligne 24/7."
        keywords="contact salon Sainte-Thérèse, réservation coiffure Sainte-Thérèse, rendez-vous esthétique Sainte-Thérèse, Luxéa Coiffure YT téléphone"
        path="/contact"
        breadcrumbs={[
          { name: "Accueil", url: "/" },
          { name: "Contact", url: "/contact" },
        ]}
      />
      <WhyUs />
      <Contact hours={hours} />
    </PageWrap>
  );
};

// 404 Page — proper "page introuvable" instead of silent redirect (SEO best practice)
const NotFoundPage = () => (
  <PageWrap>
    <SEO
      title="Page introuvable"
      description="La page que vous cherchez n'existe pas. Retournez à l'accueil de Luxéa Esthétique & Coiffure YT à Sainte-Thérèse."
      path="/404"
    />
    <section className="py-24 md:py-32 bg-white min-h-[60vh] flex items-center">
      <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500 mb-4">Erreur 404</p>
        <h1 className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl font-light tracking-tight mb-6">Page introuvable</h1>
        <p className="text-base font-light text-neutral-600 mb-10">
          Désolé, cette page n'existe plus ou a été déplacée. Retournez à l'accueil ou parcourez nos services.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/">
            <Button className="rounded-none bg-black text-white hover:bg-neutral-800 px-8 py-3 text-sm font-medium tracking-wide uppercase">
              Retour à l'accueil
            </Button>
          </Link>
          <Link to="/contact">
            <Button variant="outline" className="rounded-none border-black text-black hover:bg-black hover:text-white px-8 py-3 text-sm font-medium tracking-wide uppercase">
              Nous contacter
            </Button>
          </Link>
        </div>
      </div>
    </section>
  </PageWrap>
);

// Main App Component
function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              {/* Public site (multi-page) */}
              <Route element={<Layout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/a-propos" element={<AboutPage />} />
                <Route path="/coiffure-yt" element={<CoiffureYtPage />} />
                <Route path="/luxea-esthetique" element={<LuxeaPage />} />
                <Route path="/galerie" element={<GalleryPage />} />
                <Route path="/conditions" element={<PolicyPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>

              {/* Admin */}
              <Route path="/admin" element={<Navigate to="/admin/luxea" replace />} />
              <Route path="/admin/login" element={<Navigate to="/admin/luxea" replace />} />
              <Route path="/admin/dashboard" element={<Navigate to="/admin/luxea/dashboard" replace />} />
              <Route path="/admin/:brand" element={<AdminLogin />} />
              <Route path="/admin/:brand/dashboard" element={<AdminDashboard />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
